import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import PageSpinner from "../components/shared/PageSpinner";
import { fetchMyServiceRequests } from "../services/serviceRequestService";
import { ServiceRequest } from "../types/store";
import { toast } from "../utils/toast";
import "../styles/pages/ServiceTrackingPage.css";

const formatDate = (value: string) => {
  if (!value) {
    return "Not available";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const ServiceTrackingPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);

  useEffect(() => {
    let mounted = true;

    fetchMyServiceRequests()
      .then((items) => {
        if (!mounted) {
          return;
        }
        setRequests(items);
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }
        if (error instanceof AxiosError && error.response?.status === 401) {
          toast.error("Please log in to view your service tracking.");
          navigate("/login", { replace: true, state: { from: "/service-tracking" } });
          return;
        }
        toast.error("Unable to load service tracking right now.");
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [navigate]);

  const latestRequests = useMemo(() => requests.slice(0, 20), [requests]);

  if (loading) {
    return (
      <section className="shell section page-section service-tracking-page">
        <PageSpinner label="Loading service tracking..." />
      </section>
    );
  }

  return (
    <section className="shell section page-section service-tracking-page">
      <MobilePageHeader title="Service Tracking" />
      {latestRequests.length ? (
        <div className="service-tracking-list">
          {latestRequests.map((request) => (
            <Link key={request.id} className="service-tracking-card service-tracking-card--summary" to={`/service-tracking/${request.id}`}>
              <span>{request.serviceName}</span>
              <strong>{formatDate(request.createdAt)}</strong>
            </Link>
          ))}
        </div>
      ) : (
        <div className="service-tracking-empty">
          <h2>No service bookings yet</h2>
          <p>Once you submit a service request, its review and status updates will appear here.</p>
          <Link className="button" to="/services">
            Book your first service
          </Link>
        </div>
      )}
    </section>
  );
};

export default ServiceTrackingPage;
